import { ProvisioningMonthlyTotal, ProvisioningReportRow } from 'src/app/models/dtos/provisioning-report';

/**
 * ICES-39 — regras da tela de Provisionamento de Receitas Fixas (as mesmas do front novo):
 * intervalo em meses, filtros de cliente e título aplicados no front e totais por mês.
 */

/** 'yyyy-MM-dd' (ou 'yyyy-MM-ddTHH:mm:ss') -> 'MM/yyyy'. Lê a string, sem passar por fuso. */
export function provisioningMonthLabel(value: string): string {
  if (!value) {
    return '';
  }
  const [year, month] = value.substring(0, 10).split('-');
  return year && month ? `${month}/${year}` : value;
}

/** 'yyyy-MM-dd' (ou 'yyyy-MM-ddTHH:mm:ss') -> 'dd/MM/yyyy'. Vazio quando não há data. */
export function provisioningDayLabel(value: string): string {
  if (!value) {
    return '';
  }
  const [year, month, day] = value.substring(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
}

/**
 * Epoch millis do 1º dia (12h, hora local) dos meses inicial e final. O backend só usa o mês;
 * o meio-dia evita que o fuso empurre a data para o mês anterior.
 */
export function provisioningRange(start: Date, end: Date): { fromMs: number, toMs: number } {
  return {
    fromMs: new Date(start.getFullYear(), start.getMonth(), 1, 12).getTime(),
    toMs: new Date(end.getFullYear(), end.getMonth(), 1, 12).getTime(),
  };
}

/** Cliente e título filtram no front (o backend não os recebe). Título por trecho do número. */
export function filterProvisioningRows(
  rows: ProvisioningReportRow[],
  customerIds: number[],
  titleNumber: string
): ProvisioningReportRow[] {
  const customers = (customerIds || []).filter((id) => id != null);
  const title = (titleNumber || '').trim();
  return (rows || []).filter((row) =>
    (customers.length === 0 || customers.includes(row.customerId)) &&
    (title === '' || String(row.titleNumber ?? '').includes(title))
  );
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Totais por mês (ordem crescente) e total do período, somando as linhas exibidas (já filtradas).
 * Os meses do período que o backend devolveu em monthlyTotals aparecem mesmo quando ficam zerados.
 */
export function provisioningTotals(
  rows: ProvisioningReportRow[],
  periodMonths: ProvisioningMonthlyTotal[] = []
): { monthlyTotals: ProvisioningMonthlyTotal[], periodTotal: number } {
  const byMonth = new Map<string, number>();
  (periodMonths || []).forEach((m) => {
    if (m?.referenceMonth) {
      byMonth.set(m.referenceMonth.substring(0, 10), 0);
    }
  });
  let periodTotal = 0;
  (rows || []).forEach((row) => {
    const value = Number(row.provisionedValue) || 0;
    const month = (row.referenceMonth || '').substring(0, 10);
    byMonth.set(month, (byMonth.get(month) ?? 0) + value);
    periodTotal += value;
  });
  const monthlyTotals = [...byMonth.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([referenceMonth, total]) => ({ referenceMonth, total: round2(total) }));
  return { monthlyTotals, periodTotal: round2(periodTotal) };
}
